import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "util/services";

/** Decision Analysis (27), Hot Terms (28), Sentiment (11), Intent (12): Mongo snapshot; widget dates. */
function isWelcomeMongoSnapshotMetricId(metricId) {
  const n = Number(metricId);
  return n === 11 || n === 12 || n === 27 || n === 28;
}

/** GenAI metrics (20, 21, 23, 25): Mongo/worker scope uses widget-stored since/until. */
export function isWelcomeGenaiMetricId(metricId) {
  const n = Number(metricId);
  return n === 20 || n === 21 || n === 23 || n === 25;
}

/**
 * KPI POST uses widget row since/until when set (not Welcome page URL dates).
 * Includes headline brand KPIs (8–10, 29) so regenerate/live recompute matches wizard scope.
 * Same scope as multi-topic Sentiment (14): Participation (15) and Mongo snapshot metrics.
 */
function isWelcomeWidgetStoredDateKpiMetricId(metricId) {
  const n = Number(metricId);
  return (
    n === 8 ||
    n === 9 ||
    n === 10 ||
    n === 11 ||
    n === 12 ||
    n === 14 ||
    n === 15 ||
    n === 27 ||
    n === 28 ||
    n === 29
  );
}

function formatWelcomeWidgetDateForKpiKey(d) {
  if (d == null) return "";
  if (typeof d === "string") return d.slice(0, 10);
  return moment(d).format("YYYY-MM-DD");
}

/**
 * Normalize pivot values to strings so KPI cache keys match backend (e.g. adultCount: ['2']).
 */
function normalizePivotFilterValues(pivot) {
  if (pivot == null) return pivot;
  let source = pivot;
  if (typeof pivot === "string" && pivot.trim()) {
    try {
      source = JSON.parse(pivot);
    } catch {
      return null;
    }
  }
  if (typeof source !== "object" || Array.isArray(source)) {
    return source;
  }
  const out = {};
  Object.entries(source).forEach(([col, vals]) => {
    if (vals == null) return;
    const arr = Array.isArray(vals) ? vals : [vals];
    out[String(col)] = arr.map((v) => String(v));
  });
  return Object.keys(out).length ? out : null;
}

/**
 * When URL has no pivotFilter, use widget row pivot so POST body matches server pivot_eff,
 * logs align, and KPI cache keys update when the widget pivot changes.
 */
export function resolveWelcomePivotFilterForKpi(widget, pivotFilterPayload) {
  if (pivotFilterPayload != null) {
    return normalizePivotFilterValues(pivotFilterPayload);
  }
  const fromStoredFilter = normalizePivotFilterValues(widget?.pivot_filter);
  if (fromStoredFilter && Object.keys(fromStoredFilter).length > 0) {
    return fromStoredFilter;
  }
  const k = widget?.pivot_key;
  const vals = widget?.pivot_values;
  if (!k || vals == null) return null;
  const arr = Array.isArray(vals) ? vals : [vals];
  if (arr.length === 0) return null;
  return { [String(k)]: arr.map((v) => String(v)) };
}

/**
 * welcome/widget/data body: GenAI (20,21,23,25), Mongo snapshot metrics (11,12,27,28),
 * Sentiment (14), and Participation per Topic (15) use widget-stored since/until when present;
 * otherwise page dates or (Mongo only) omit dates.
 */
export function buildWelcomeWidgetDataKpiPayload(
  widget,
  pageSince,
  pageUntil,
  userId,
  pivotFilterPayload,
  pivotDashboardIdRaw,
) {
  const base = {
    widget_id: widget.id,
    user_id: userId,
    pivot_filter: resolveWelcomePivotFilterForKpi(widget, pivotFilterPayload),
    ...(pivotDashboardIdRaw
      ? { pivot_dashboard_id: pivotDashboardIdRaw }
      : {}),
  };
  if (
    !isWelcomeWidgetStoredDateKpiMetricId(widget?.metric_id) &&
    !isWelcomeGenaiMetricId(widget?.metric_id)
  ) {
    return { ...base, since: pageSince, until: pageUntil };
  }
  const ws = widget?.since ?? widget?.start_date ?? widget?.date_from;
  const wu = widget?.until ?? widget?.end_date ?? widget?.date_to;
  if (ws && wu) {
    return { ...base, since: ws, until: wu };
  }
  if (isWelcomeMongoSnapshotMetricId(widget?.metric_id)) {
    return base;
  }
  return { ...base, since: pageSince, until: pageUntil };
}

/**
 * Multi-topic lazy KPI: chart metrics with General + per-topic tabs (see API
 * WELCOME_LAZY_TOPIC_TAB_BRAND_METRIC_IDS); representation_type 1 only.
 */
export function welcomeWidgetUsesLazyTopicTabs(widget) {
  const mid = Number(widget?.metric_id);
  const rep = Number(widget?.representation_type);
  const tids = Array.isArray(widget?.topic_ids)
    ? widget.topic_ids.map((x) => Number(x)).filter((n) => n > 0)
    : [];
  if (tids.length === 0) return false;
  if (rep !== 1) return false;
  return [8, 9, 10, 11, 12, 13, 14, 15, 16, 18, 27, 28, 29].includes(mid);
}

/** Main dashboard KPI fetch body: same as legacy fetchWelcomeWidgetDataForWidget (general scope for lazy tabs). */
export function buildWelcomeWidgetMainFetchPayload(
  widget,
  pageSince,
  pageUntil,
  userId,
  pivotFilterPayload,
  pivotDashboardIdRaw,
) {
  const payload = {
    ...buildWelcomeWidgetDataKpiPayload(
      widget,
      pageSince,
      pageUntil,
      userId,
      pivotFilterPayload,
      pivotDashboardIdRaw,
    ),
  };
  if (welcomeWidgetUsesLazyTopicTabs(widget)) {
    payload.topic_tab_scope = "general";
  }
  return payload;
}

/** Cache key for KPI fetch: snapshot metrics ignore Welcome URL date; 14/15 use widget dates when set. */
export function getWelcomeWidgetKpiQueryKey(
  widget,
  pageSince,
  pageUntil,
  pivotFilterPayload,
  pivotDashboardIdRaw,
) {
  const resolvedPivot = resolveWelcomePivotFilterForKpi(
    widget,
    pivotFilterPayload,
  );
  const p = resolvedPivot == null ? "" : JSON.stringify(resolvedPivot);
  const pd = pivotDashboardIdRaw || "";
  if (isWelcomeMongoSnapshotMetricId(widget?.metric_id)) {
    return `mongo=${widget.id}&p=${p}&pd=${pd}`;
  }
  const ws = widget?.since ?? widget?.start_date ?? widget?.date_from;
  const wu = widget?.until ?? widget?.end_date ?? widget?.date_to;
  if (
    (isWelcomeWidgetStoredDateKpiMetricId(widget?.metric_id) ||
      isWelcomeGenaiMetricId(widget?.metric_id)) &&
    !isWelcomeMongoSnapshotMetricId(widget?.metric_id) &&
    ws &&
    wu
  ) {
    return `w=${widget.id}&s=${formatWelcomeWidgetDateForKpiKey(
      ws,
    )}&u=${formatWelcomeWidgetDateForKpiKey(wu)}&p=${p}&pd=${pd}`;
  }
  return `s=${pageSince}&u=${pageUntil}&p=${p}&pd=${pd}`;
}

export const welcomeWidgetKeys = {
  all: (uid) => ["welcome", "widgets", uid ?? "anonymous"],
  /** One cache entry per widget + logical KPI scope string from getWelcomeWidgetKpiQueryKey. */
  mainData: (uid, widgetId, kpiQueryKeyStr) => [
    ...welcomeWidgetKeys.all(uid),
    "main",
    Number(widgetId),
    kpiQueryKeyStr,
  ],
  /** Batch POST welcome/group/{groupId}/widgets/data — scoped by KPI context + widget set. */
  groupBatch: (uid, groupId, contextKey) => [
    ...welcomeWidgetKeys.all(uid),
    "groupBatch",
    Number(groupId),
    contextKey,
  ],
};

/**
 * Subscribe to the parent-owned main KPI cache entry (no network — fetch runs in GroupWidgets useQueries / batch).
 */
export function useWelcomeWidgetCache(plan) {
  const canSubscribe = Boolean(
    plan &&
      Array.isArray(plan.queryKey) &&
      plan.queryKey.length > 0,
  );
  return useQuery({
    queryKey: canSubscribe
      ? plan.queryKey
      : ["welcome", "widgets", "_no_kpi_plan"],
    queryFn: async () => {
      throw new Error("useWelcomeWidgetCache is subscribe-only");
    },
    enabled: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Parent-level reads (menus, quota): merge row `data` with KPI envelope from TanStack cache.
 * After removing the RQ→React-state bridge, `widgetRow.data` is often empty while `envelope.data` holds KPI.
 */
export function mergeWelcomeWidgetRowDataWithKpiEnvelope(widgetRow, envelope) {
  const rowData =
    widgetRow?.data && typeof widgetRow.data === "object" ? widgetRow.data : {};
  const cacheData =
    envelope?.data && typeof envelope.data === "object" ? envelope.data : {};
  if (Object.keys(cacheData).length === 0) {
    return rowData;
  }
  return { ...rowData, ...cacheData };
}

/** Merge partial welcome/widget/data (topic_tab_partial / summary_tabs) into a widget-shaped row. */
export function mergeWelcomePartialTopicData(prevWidget, partialKpi) {
  if (!partialKpi || typeof partialKpi !== "object") return prevWidget;
  const prev = prevWidget.data || {};
  if (partialKpi.topic_tabs && partialKpi.topic_tab_partial) {
    const byId = new Map(
      partialKpi.topic_tabs.map((tab) => [String(tab.id), tab]),
    );
    return {
      ...prevWidget,
      data: {
        ...prev,
        topic_tabs: (prev.topic_tabs || []).map((tab) => {
          const u = byId.get(String(tab.id));
          if (!u) return tab;
          return {
            ...tab,
            ...u,
            payload_pending: false,
          };
        }),
      },
    };
  }
  if (partialKpi.summary_tabs && partialKpi.topic_tab_partial) {
    const byId = new Map(
      partialKpi.summary_tabs.map((tab) => [String(tab.id), tab]),
    );
    return {
      ...prevWidget,
      data: {
        ...prev,
        summary_tabs: (prev.summary_tabs || []).map((tab) => {
          const u = byId.get(String(tab.id));
          if (!u) return tab;
          return {
            ...tab,
            ...u,
            payload_pending: false,
            content_pending: false,
          };
        }),
      },
    };
  }
  return prevWidget;
}

const WELCOME_WIDGET_DATA_MAX_CONCURRENT = 10;
let welcomeWidgetDataInflight = 0;
const welcomeWidgetDataWaitQueue = [];

function pumpWelcomeWidgetDataQueue() {
  while (
    welcomeWidgetDataInflight < WELCOME_WIDGET_DATA_MAX_CONCURRENT &&
    welcomeWidgetDataWaitQueue.length > 0
  ) {
    const job = welcomeWidgetDataWaitQueue.shift();
    if (job.signal?.aborted) {
      job.reject(new Error("WELCOME_WIDGET_DATA_CANCELLED"));
      continue;
    }
    welcomeWidgetDataInflight += 1;
    job
      .run()
      .then(job.resolve, job.reject)
      .finally(() => {
        welcomeWidgetDataInflight -= 1;
        pumpWelcomeWidgetDataQueue();
      });
  }
}

function enqueueWelcomeWidgetDataJob(signal, run) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("WELCOME_WIDGET_DATA_CANCELLED"));
      return;
    }
    const onAbort = () => {
      reject(new Error("WELCOME_WIDGET_DATA_CANCELLED"));
    };
    if (signal) {
      signal.addEventListener("abort", onAbort, { once: true });
    }
    const job = {
      signal,
      run: async () => {
        try {
          return await run();
        } finally {
          if (signal) {
            signal.removeEventListener("abort", onAbort);
          }
        }
      },
      resolve: (v) => {
        if (signal) {
          signal.removeEventListener("abort", onAbort);
        }
        resolve(v);
      },
      reject: (e) => {
        if (signal) {
          signal.removeEventListener("abort", onAbort);
        }
        reject(e);
      },
    };
    welcomeWidgetDataWaitQueue.push(job);
    pumpWelcomeWidgetDataQueue();
  });
}

export async function fetchWelcomeWidgetData(payload, signal) {
  return enqueueWelcomeWidgetDataJob(signal, async () => {
    const response = await apiRequest(
      "welcome/widget/data",
      "POST",
      payload,
      true,
      signal,
    );
    if (response === false || response?.cancel) {
      throw new Error("WELCOME_WIDGET_DATA_CANCELLED");
    }
    if (response?.success == null) {
      throw new Error("WELCOME_WIDGET_DATA_FAILED");
    }
    return response.success;
  });
}

/** POST welcome/group/widgets/data — one round-trip for all widgets in a group. */
export function buildWelcomeGroupWidgetsBatchPayload({
  groupId,
  userId,
  pageSince,
  pageUntil,
  pivotFilterPayload,
  widgetIds,
}) {
  return {
    group_id: Number(groupId),
    user_id: userId,
    since: pageSince,
    until: pageUntil,
    pivot_filter: pivotFilterPayload ?? null,
    widget_ids: Array.isArray(widgetIds)
      ? widgetIds.map((id) => Number(id)).filter((n) => n > 0)
      : undefined,
  };
}

const WELCOME_GROUP_WIDGETS_DATA_CHUNK_SIZE = 6;

async function postWelcomeGroupWidgetsData(payload, signal) {
  const response = await apiRequest(
    "welcome/group/widgets/data",
    "POST",
    payload,
    true,
    signal,
  );
  if (response === false || response?.cancel) {
    throw new Error("WELCOME_WIDGET_DATA_CANCELLED");
  }
  if (response?.success == null) {
    throw new Error("WELCOME_GROUP_WIDGETS_DATA_FAILED");
  }
  return response.success;
}

export async function fetchWelcomeGroupWidgetsData(payload, signal) {
  const widgetIds = Array.isArray(payload?.widget_ids)
    ? payload.widget_ids.map((id) => Number(id)).filter((n) => n > 0)
    : null;

  if (
    widgetIds &&
    widgetIds.length > WELCOME_GROUP_WIDGETS_DATA_CHUNK_SIZE
  ) {
    return enqueueWelcomeWidgetDataJob(signal, async () => {
      const merged = [];
      for (
        let i = 0;
        i < widgetIds.length;
        i += WELCOME_GROUP_WIDGETS_DATA_CHUNK_SIZE
      ) {
        if (signal?.aborted) {
          throw new DOMException("Aborted", "AbortError");
        }
        const chunkIds = widgetIds.slice(
          i,
          i + WELCOME_GROUP_WIDGETS_DATA_CHUNK_SIZE,
        );
        const part = await postWelcomeGroupWidgetsData(
          { ...payload, widget_ids: chunkIds },
          signal,
        );
        if (Array.isArray(part?.widgets)) {
          merged.push(...part.widgets);
        }
      }
      return { widgets: merged };
    });
  }

  return enqueueWelcomeWidgetDataJob(signal, async () =>
    postWelcomeGroupWidgetsData(payload, signal),
  );
}

/**
 * Seed per-widget mainData cache entries from a batch response so existing
 * per-widget UI (useQueries / getWelcomeWidgetEffectiveKpiData) keeps working.
 * Returns widget ids that were written into the cache.
 */
export function seedWelcomeWidgetMainDataFromBatch(
  queryClient,
  uid,
  plans,
  batchSuccess,
) {
  const widgets = batchSuccess?.widgets;
  if (!queryClient || !uid || !Array.isArray(widgets) || !Array.isArray(plans)) {
    return new Set();
  }
  const planById = new Map(
    plans.map((plan) => [Number(plan.widgetRow?.id), plan]),
  );
  const seeded = new Set();
  widgets.forEach((envelope) => {
    const wid = Number(envelope?.id);
    if (!wid) return;
    const plan = planById.get(wid);
    if (!plan?.queryKey) return;
    queryClient.setQueryData(plan.queryKey, envelope);
    seeded.add(wid);
  });
  return seeded;
}

/**
 * Reload widget KPI / Mongo snapshot / GenAI display payload from the server
 * (welcome/widget/data). Does not queue LLM jobs — unlike genai-requeue.
 */
export function clearWelcomeWidgetMainDataCache(queryClient, queryKey) {
  if (!queryClient || !Array.isArray(queryKey) || queryKey.length === 0) {
    return;
  }
  queryClient.setQueryData(queryKey, undefined);
  queryClient.removeQueries({ queryKey, exact: true });
}

export async function refetchWelcomeWidgetMainData(queryClient, queryKey) {
  if (!queryClient || !Array.isArray(queryKey) || queryKey.length === 0) {
    return;
  }
  clearWelcomeWidgetMainDataCache(queryClient, queryKey);
  await queryClient.refetchQueries({ queryKey, exact: true });
}
